// ============================================================================
// Copia local para la caja de contingencia (modo contingencia, paso 3b).
//
// No pinta nada. Mientras hay internet, cada 2 minutos (y unos segundos
// después de cada cambio en pedidos o mesas) guarda en el PC la carta, las
// mesas y los pedidos abiertos de la sede. Es lo que la caja de contingencia
// usa cuando se cae la red. La lógica está en src/lib/copia-local.ts.
//
// Va como componente aparte, y no como hook dentro de App, para que su
// estado no haga repintar toda la app.
// ============================================================================
import { useEffect, useRef } from 'react';
import { useUser } from '../context/user-context';
import { useBranch } from '../context/branch-context';
import { useRealtime } from '../hooks/use-realtime';
import { conexionCaida, marcarCopiaTomada } from '../hooks/use-conexion';
import { listMenu } from '../../api/menu';
import { listMesas } from '../../api/mesas';
import { listPedidos, getPedido } from '../../api/pedidos';
import { guardarCopia, leerCopia } from '../../lib/almacen-local';
import { tomarCopia, claveCopia, COPIA_CADA_MS, type CopiaLocal } from '../../lib/copia-local';

const ESPERA_TRAS_CAMBIO_MS = 5_000;

export function CopiaLocalContingencia() {
  const { user } = useUser();
  const { currentBranchId } = useBranch();
  const enCurso = useRef(false);
  const tras = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sedeRef = useRef<number | null>(null);
  sedeRef.current = currentBranchId;

  const tomar = async () => {
    const sedeId = sedeRef.current;
    // Sin red no se intenta: fallaría y llenaría la pantalla de avisos.
    if (!sedeId || enCurso.current || conexionCaida()) return;
    enCurso.current = true;
    try {
      const copia = await tomarCopia(sedeId, {
        menu: (s) => listMenu({ sede_id: s }),
        mesas: (s) => listMesas(s),
        pedidosAbiertos: (s) => listPedidos({ sede_id: s, activos: true }),
        detalle: (id) => getPedido(id),
        guardar: guardarCopia,
      });
      marcarCopiaTomada(copia.tomadaEn);
    } catch (e) {
      // La copia anterior queda intacta (todo o nada). Se reintenta en el
      // próximo ciclo; no se avisa al cajero: no tiene nada que hacer con esto.
      console.warn('[contingencia] no se pudo actualizar la copia local:', e);
    } finally {
      enCurso.current = false;
    }
  };

  // Al entrar o cambiar de sede: mostrar la hora de la copia que ya había y
  // tomar una nueva; después, cada 2 minutos.
  useEffect(() => {
    if (!user || !currentBranchId) return;
    let vivo = true;
    leerCopia<CopiaLocal>(claveCopia(currentBranchId))
      .then((c) => { if (vivo && c) marcarCopiaTomada(c.tomadaEn); })
      .catch(() => { /* sin almacén local: se verá "todavía no" */ });
    void tomar();
    const reloj = setInterval(() => { void tomar(); }, COPIA_CADA_MS);
    return () => { vivo = false; clearInterval(reloj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, currentBranchId]);

  // Cambió un pedido o una mesa: copia nueva unos segundos después (una sola
  // aunque lleguen diez eventos seguidos).
  useRealtime(['pedido_creado', 'pedido_actualizado', 'mesa_actualizada'], () => {
    if (tras.current) clearTimeout(tras.current);
    tras.current = setTimeout(() => { void tomar(); }, ESPERA_TRAS_CAMBIO_MS);
  });

  useEffect(() => () => { if (tras.current) clearTimeout(tras.current); }, []);

  return null;
}
